// API service for Transcontinental Network React frontend
// Updated with OTP verification support

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://transcontinentalnetwork.com/transcontinental-api';

class ApiService {
  constructor() {
    // Use 'access_token' to match backend response
    this.token = localStorage.getItem('access_token');
  }

  // Get auth headers with proper content type
  getHeaders(includeAuth = true, contentType = 'application/json') {
    const headers = {};

    // Always include content type for POST/PUT requests
    if (contentType) {
      headers['Content-Type'] = contentType;
    }

    if (includeAuth && this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    return headers;
  }

  // Generic request method with improved error handling
  async request(endpoint, options = {}) {
    const url = `${API_BASE_URL}${endpoint}`;
    
    const config = {
      headers: this.getHeaders(options.auth !== false),
      ...options,
    };

    // Ensure we don't duplicate content-type
    if (options.body && typeof options.body === 'string') {
      config.headers['Content-Type'] = 'application/json';
    }

    try {
      console.log(`Making request to: ${url}`, {
        method: config.method || 'GET',
        headers: config.headers,
        body: config.body ? JSON.parse(config.body) : 'No body'
      });
      
      const response = await fetch(url, config);
      
      // Check if the response is JSON
      const contentType = response.headers.get('content-type');
      let data;
      
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        // Handle non-JSON responses
        const text = await response.text();
        console.error('Non-JSON response:', {
          status: response.status,
          statusText: response.statusText,
          text: text.substring(0, 200) + (text.length > 200 ? '...' : '')
        });
        
        if (response.ok) {
          return { success: true, data: text };
        }
        
        throw new Error(`Server returned non-JSON response: ${response.status} ${response.statusText}`);
      }

      console.log('API Response:', {
        status: response.status,
        success: data.success,
        message: data.message,
        hasData: !!data.data,
        fullResponse: data
      });

      // Handle HTTP errors
      if (!response.ok) {
        let errorMessage = data.message || data.error || `Request failed with status ${response.status}`;
        
        const error = new Error(errorMessage);
        error.status = response.status;
        error.code = data.error_code || data.code;
        error.details = data.details || data.errors;
        
        throw error;
      }

      return data;
      
    } catch (error) {
      console.error('API Request failed:', {
        endpoint,
        method: config.method || 'GET',
        error: error.message
      });
      
      // Handle specific network errors
      if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        const networkError = new Error('Unable to connect to server. Please check your internet connection.');
        networkError.type = 'NETWORK_ERROR';
        throw networkError;
      }
      
      // Handle authentication errors
      if (error.status === 401) {
        this.removeToken();
        const authError = new Error('Your session has expired. Please log in again.');
        authError.type = 'AUTH_ERROR';
        throw authError;
      }
      
      throw error;
    }
  }

  // Authentication methods
  async register(userData) {
    try {
      const backendData = {
        name: userData.name || `${userData.firstName || ''} ${userData.lastName || ''}`.trim(),
        email: userData.email,
        password: userData.password,
        confirmPassword: userData.confirmPassword || userData.password
      };

      console.log('Registration attempt:', {
        name: backendData.name,
        email: backendData.email
      });

      const response = await this.request('/auth/register', {
        method: 'POST',
        auth: false,
        body: JSON.stringify(backendData),
      });

      // NEW: Backend now returns user_id and email (no token yet, need OTP verification)
      if (response.success && response.data) {
        console.log('Registration successful, OTP sent to email');
        
        // Return registration data - NO TOKEN YET
        return {
          success: true,
          requiresOTP: true, // Flag to show OTP screen
          user_id: response.data.user_id,
          email: response.data.email,
          message: response.message || 'Please check your email for OTP verification code.'
        };
      }
      
      throw new Error(response.message || 'Registration failed');
      
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  }

  // NEW: Verify OTP after registration
  async verifyOTP(email, otp) {
    try {
      console.log('Verifying OTP for:', email);

      const response = await this.request('/auth/verify-otp', {
        method: 'POST',
        auth: false,
        body: JSON.stringify({ email, otp }),
      });

      // After successful OTP verification, backend returns token and user
      if (response.success && response.data) {
        const { access_token, user } = response.data;
        
        if (access_token) {
          this.setToken(access_token);
          
          if (user) {
            localStorage.setItem('user', JSON.stringify(user));
          }
          
          console.log('OTP verified, user logged in');
          
          return {
            success: true,
            access_token: access_token,
            user: user,
            message: response.message || 'Email verified successfully'
          };
        }
      }
      
      throw new Error(response.message || 'OTP verification failed');
      
    } catch (error) {
      console.error('OTP verification error:', error);
      throw error;
    }
  }

  // NEW: Resend OTP
  async resendOTP(email) {
    try {
      console.log('Resending OTP to:', email);

      const response = await this.request('/auth/resend-otp', {
        method: 'POST',
        auth: false,
        body: JSON.stringify({ email }),
      });

      if (response.success) {
        console.log('OTP resent successfully');
        return {
          success: true,
          message: response.message || 'New OTP sent to your email'
        };
      }
      
      throw new Error(response.message || 'Failed to resend OTP');
      
    } catch (error) {
      console.error('Resend OTP error:', error);
      throw error;
    }
  }

  async login(credentials) {
    try {
      console.log('Attempting login with:', { email: credentials.email });
      
      const response = await this.request('/auth/login', {
        method: 'POST',
        auth: false,
        body: JSON.stringify(credentials),
      });

      // PHP backend returns: { success: true, data: { access_token, user }, message }
      if (response.success && response.data) {
        const { access_token, user } = response.data;
        
        if (access_token) {
          this.setToken(access_token);
          
          if (user) {
            localStorage.setItem('user', JSON.stringify(user));
          }
          
          console.log('Login successful');
          
          // Return in format expected by React component
          return {
            success: true,
            access_token: access_token,
            user: user,
            message: response.message
          };
        }
      }
      
      throw new Error(response.message || 'Login failed');
      
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  async logout() {
    try {
      await this.request('/auth/logout', {
        method: 'POST',
      });
    } catch (error) {
      console.error('Logout request failed:', error);
    } finally {
      this.removeToken();
    }
  }

  async getProfile() {
    try {
      const response = await this.request('/auth/profile', {
        method: 'GET',
      });
      
      // PHP backend returns: { success: true, data: { user }, message }
      if (response.success && response.data) {
        return response.data;
      }
      
      return response;
    } catch (error) {
      console.error('Get profile error:', error);
      throw error;
    }
  }

  async updateProfile(profileData) {
    try {
      const response = await this.request('/auth/profile', {
        method: 'PUT',
        body: JSON.stringify(profileData),
      });
      
      if (response.success && response.data) {
        return response.data;
      }
      
      return response;
    } catch (error) {
      console.error('Update profile error:', error);
      throw error;
    }
  }

  async forgotPassword(email) {
    try {
      return await this.request('/auth/forgot-password', {
        method: 'POST',
        auth: false,
        body: JSON.stringify({ email }),
      });
    } catch (error) {
      console.error('Forgot password error:', error);
      throw error;
    }
  }

  async resetPassword(resetData) {
    try {
      return await this.request('/auth/reset-password', {
        method: 'POST',
        auth: false,
        body: JSON.stringify(resetData),
      });
    } catch (error) {
      console.error('Reset password error:', error);
      throw error;
    }
  }

  async changePassword(passwordData) {
    try {
      return await this.request('/auth/change-password', {
        method: 'POST',
        body: JSON.stringify(passwordData),
      });
    } catch (error) {
      console.error('Change password error:', error);
      throw error;
    }
  }

  // Token management
  setToken(token) {
    this.token = token;
    localStorage.setItem('access_token', token);
    console.log('Token set successfully');
  }

  removeToken() {
    this.token = null;
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
    console.log('Token and user data cleared');
  }

  getToken() {
    this.token = localStorage.getItem('access_token');
    return this.token;
  }

  isAuthenticated() {
    const token = this.getToken();
    return !!token && token !== 'null' && token !== 'undefined';
  }

  getUser() {
    try {
      const userStr = localStorage.getItem('user');
      return userStr ? JSON.parse(userStr) : null;
    } catch (error) {
      console.error('Error parsing user data:', error);
      localStorage.removeItem('user');
      return null;
    }
  }

  // Health check
  async healthCheck() {
    try {
      const response = await this.request('/health', {
        method: 'GET',
        auth: false,
      });
      return response;
    } catch (error) {
      console.error('Health check error:', error);
      throw error;
    }
  }

  async testConnection() {
    try {
      console.log('Testing connection to server...');
      const response = await this.healthCheck();
      console.log('Connection test successful:', response);
      return response;
    } catch (error) {
      console.error('Connection test failed:', error);
      throw error;
    }
  }
}

export default new ApiService();